(function () {
  const NAMESPACE = 'ZIG_CART_PRICING_BRIDGE';
  const PATCH_FLAG = '__zigCartPricingBridgePatched';

  function getConfig() {
    return window[NAMESPACE] || {};
  }

  function isEnabled() {
    const config = getConfig();
    return Boolean(config.enabled && config.normalizeCartForB2C && !config.isB2B);
  }

  function toNumber(value, fallback) {
    const number = Number(value);
    return Number.isFinite(number) ? number : fallback;
  }

  function normalizeVariantId(value) {
    const raw = String(value || '').trim();
    if (!raw) return '';
    const match = raw.match(/ProductVariant\/(\d+)/);
    if (match && match[1]) return match[1];
    return raw.replace(/\D/g, '');
  }

  function mergeConfig(nextConfig) {
    const current = getConfig();
    window[NAMESPACE] = {
      ...current,
      ...nextConfig,
      lineTaxRates: {
        ...(current.lineTaxRates || {}),
        ...(nextConfig.lineTaxRates || {})
      },
      variantTaxRates: {
        ...(current.variantTaxRates || {}),
        ...(nextConfig.variantTaxRates || {})
      }
    };
  }

  function scanDomTaxRates(root) {
    const container = root && root.querySelectorAll ? root : document;
    const lineTaxRates = {};
    const variantTaxRates = {};

    container.querySelectorAll('[data-zig-cart-line-key][data-zig-vat-rate]').forEach((node) => {
      const lineKey = node.getAttribute('data-zig-cart-line-key');
      const variantId = normalizeVariantId(node.getAttribute('data-variant-id'));
      const vatRate = toNumber(node.getAttribute('data-zig-vat-rate'), NaN);

      if (!Number.isFinite(vatRate)) return;
      if (lineKey) lineTaxRates[lineKey] = vatRate;
      if (variantId) variantTaxRates[variantId] = vatRate;
    });

    if (Object.keys(lineTaxRates).length || Object.keys(variantTaxRates).length) {
      mergeConfig({ lineTaxRates, variantTaxRates });
    }
  }

  function getTaxRateForItem(item) {
    const config = getConfig();
    const lineTaxRates = config.lineTaxRates || {};
    const variantTaxRates = config.variantTaxRates || {};
    const key = item && item.key ? String(item.key) : '';
    const variantId = normalizeVariantId(item && item.variant_id);

    if (key && lineTaxRates[key] != null) return toNumber(lineTaxRates[key], 0);
    if (variantId && variantTaxRates[variantId] != null) return toNumber(variantTaxRates[variantId], 0);

    return toNumber(config.defaultVatRate, 0);
  }

  function grossifyCents(value, vatRate) {
    const cents = Number(value || 0);
    if (!Number.isFinite(cents) || cents <= 0) return cents || 0;
    return Math.round(cents * (1 + vatRate / 100));
  }

  function normalizeItemPrices(item) {
    if (!item || item.__zig_price_normalized) return item;

    const vatRate = getTaxRateForItem(item);
    const out = { ...item };

    [
      'price',
      'original_price',
      'discounted_price',
      'final_price',
      'line_price',
      'original_line_price',
      'discounted_price',
      'final_line_price'
    ].forEach((field) => {
      if (out[field] != null) {
        out[field] = grossifyCents(out[field], vatRate);
      }
    });

    if (Array.isArray(out.options_with_values)) {
      out.options_with_values = out.options_with_values.map((option) => ({ ...option }));
    }

    out.__zig_vat_rate = vatRate;
    out.__zig_price_normalized = true;

    return out;
  }

  function normalizeCart(cart) {
    if (!isEnabled() || !cart || cart.__zig_price_normalized || !Array.isArray(cart.items)) {
      return cart;
    }

    scanDomTaxRates(document);

    const normalized = {
      ...cart,
      items: cart.items.map(normalizeItemPrices)
    };

    const sum = (field) => normalized.items.reduce((total, item) => total + Number(item[field] || 0), 0);

    normalized.items_subtotal_price = sum('final_line_price');
    normalized.original_total_price = sum('original_line_price') || sum('line_price');
    normalized.total_price = sum('final_line_price');
    normalized.total_discount = Math.max(0, normalized.original_total_price - normalized.total_price);
    normalized.__zig_price_normalized = true;
    normalized.__zig_pricing_bridge = {
      mode: 'b2c-gross-from-theme',
      source: 'theme-side-fetch-bridge'
    };

    return normalized;
  }

  function isCartJsRequest(input, init) {
    const method = String((init && init.method) || (input && input.method) || 'GET').toUpperCase();
    if (method !== 'GET') return false;

    const rawUrl = typeof input === 'string' ? input : input && input.url;
    if (!rawUrl) return false;

    try {
      const url = new URL(rawUrl, window.location.origin);
      return /\/cart\.js$/.test(url.pathname);
    } catch (error) {
      return String(rawUrl).includes('/cart.js');
    }
  }

  function responseWithJson(originalResponse, payload) {
    const headers = new Headers(originalResponse.headers);
    headers.set('Content-Type', 'application/json; charset=utf-8');

    return new Response(JSON.stringify(payload), {
      status: originalResponse.status,
      statusText: originalResponse.statusText,
      headers
    });
  }

  function patchFetch() {
    if (!window.fetch || window[PATCH_FLAG]) return;
    window[PATCH_FLAG] = true;

    const originalFetch = window.fetch;

    window.fetch = async function zigPatchedFetch(input, init) {
      const response = await originalFetch.apply(this, arguments);

      if (!isEnabled() || !isCartJsRequest(input, init)) {
        return response;
      }

      try {
        const cart = await response.clone().json();
        const normalizedCart = normalizeCart(cart);
        return responseWithJson(response, normalizedCart);
      } catch (error) {
        return response;
      }
    };
  }

  function boot() {
    patchFetch();
    scanDomTaxRates(document);

    window[NAMESPACE] = {
      ...getConfig(),
      mergeConfig,
      normalizeCart,
      scanDomTaxRates
    };

    document.addEventListener('shopify:section:load', (event) => {
      scanDomTaxRates(event.target || document);
    });

    document.addEventListener('cart:update', (event) => {
      scanDomTaxRates(document);
      if (event && event.detail) {
        if (event.detail.cart && Array.isArray(event.detail.cart.items)) {
          event.detail.cart = normalizeCart(event.detail.cart);
        }
        if (event.detail.resource && Array.isArray(event.detail.resource.items)) {
          event.detail.resource = normalizeCart(event.detail.resource);
        }
      }
    }, true);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', boot, { once: true });
  } else {
    boot();
  }
})();
